import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { X, UserPlus, XCircle, Download, CheckCircle, Mail, Trash2, RotateCcw, ArchiveRestore } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isOpsTeam, isAdmin, isInternalStaff } from '../utils/roles';

const BulkActionsBar = ({ 
  selectedCount, 
  onClearSelection,
  onAssignOperator,
  onBulkCancel,
  onBulkPayment,
  onBulkComplete,
  onExport,
  onSendPaymentReminder,
  onBulkDelete,
  onBulkRestoreFromCancelled,
  onBulkUnarchive,
  onBulkRestoreFromTrash
}) => {
  const { user } = useAuth();

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-0 left-64 right-0 bg-white border-t-2 border-[#1db584] shadow-lg z-50">
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-4">
          <Badge className="bg-[#1db584] text-white text-base px-4 py-2">
            ✓ {selectedCount} opération{selectedCount > 1 ? 's' : ''} sélectionnée{selectedCount > 1 ? 's' : ''}
          </Badge>
          
          <div className="flex gap-2">
            {/* Assigner opérateur - Admin + Opérateur (RBAC V2) */}
            {isOpsTeam(user?.role) && (
              <Button
                onClick={onAssignOperator}
                variant="outline"
                size="sm"
                className="hover:bg-purple-50"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assigner opérateur
              </Button>
            )}

            {/* Annuler (Admin seulement) */}
            {user?.role === 'admin' && (
              <Button
                onClick={onBulkCancel}
                variant="outline"
                size="sm"
                className="hover:bg-red-50 text-red-600"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}

            {/* Supprimer (Admin seulement) */}
            {user?.role === 'admin' && onBulkDelete && (
              <Button
                onClick={onBulkDelete}
                variant="outline"
                size="sm"
                className="hover:bg-red-100 text-red-700 border-red-300"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            )}

            {/* Rappel de Paiement (Admin + Commercial) */}
            {(user?.role === 'admin' || user?.role === 'commercial') && onSendPaymentReminder && (
              <Button
                onClick={onSendPaymentReminder}
                variant="outline"
                size="sm"
                className="hover:bg-amber-50 text-amber-600"
              >
                <Mail className="w-4 h-4 mr-2" />
                Rappel Paiement
              </Button>
            )}

            {/* Marquer comme payé (Admin + Commercial) */}
            {(user?.role === 'admin' || user?.role === 'commercial') && (
              <Button
                onClick={onBulkPayment}
                variant="outline"
                size="sm"
                className="hover:bg-green-50 text-green-600"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Marquer payé
              </Button>
            )}

            {/* Terminer (Admin) */}
            {user?.role === 'admin' && (
              <Button
                onClick={onBulkComplete}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 text-blue-600"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Terminer
              </Button>
            )}

            {/* Export (RBAC V2 — staff interne) */}
            {isInternalStaff(user?.role) && (
              <Button
                onClick={onExport}
                variant="outline"
                size="sm"
                className="hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            )}

            {/* Restaurer depuis annulation (admin/lead_ops/lead_commercial, sélection homogène d'annulées) */}
            {onBulkRestoreFromCancelled && (
              <Button
                onClick={onBulkRestoreFromCancelled}
                variant="outline"
                size="sm"
                className="hover:bg-emerald-50 text-emerald-700 border-emerald-300"
                data-testid="bulk-restore-cancelled-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restaurer ({selectedCount})
              </Button>
            )}

            {/* Désarchiver en masse (admin/commercial, sélection homogène d'archivées) */}
            {onBulkUnarchive && (
              <Button
                onClick={onBulkUnarchive}
                variant="outline"
                size="sm"
                className="hover:bg-blue-50 text-blue-700 border-blue-300"
                data-testid="bulk-unarchive-btn"
              >
                <ArchiveRestore className="w-4 h-4 mr-2" />
                Désarchiver ({selectedCount})
              </Button>
            )}

            {/* Restaurer depuis corbeille (admin, sélection homogène d'opérations soft-deleted) */}
            {onBulkRestoreFromTrash && (
              <Button
                onClick={onBulkRestoreFromTrash}
                variant="outline"
                size="sm"
                className="hover:bg-emerald-50 text-emerald-700 border-emerald-300"
                data-testid="bulk-restore-trash-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Restaurer corbeille ({selectedCount})
              </Button>
            )}
          </div>
        </div>

        <Button
          onClick={onClearSelection}
          variant="ghost"
          size="sm"
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-4 h-4 mr-1" />
          Annuler la sélection
        </Button>
      </div>
    </div>
  );
};

export default BulkActionsBar;
